/**
 * Telemetry API — client functions for the telemetry system.
 *
 * - getTelemetryConfig(): check if telemetry is enabled for the current user's org
 * - sendTelemetryBatch(): send a batch of events to the server
 * - getTelemetryOrgs(): list all orgs with telemetry status (ADMIN only)
 * - toggleOrgTelemetry(): enable/disable telemetry for an org (ADMIN only)
 * - getTelemetryEvents(): query stored events (ADMIN only)
 */

import { api } from './client';
import type {
  TelemetryConfigDto,
  TelemetryOrgInfoDto,
  TelemetryEventResponseDto,
  TelemetryEventType,
  TelemetrySeverity,
} from '@flashcard/shared';

// ============================================================
// Client endpoints
// ============================================================

/** GET /telemetry/config — check if telemetry is enabled */
export function getTelemetryConfig(): Promise<TelemetryConfigDto> {
  return api.get<TelemetryConfigDto>('/telemetry/config');
}

/** POST /telemetry/events — send a batch of telemetry events */
export function sendTelemetryBatch(
  events: TelemetryEventInput[],
  sessionId: string,
): Promise<{ stored: number }> {
  return api.post<{ stored: number }>('/telemetry/events', { events, sessionId });
}

// ============================================================
// Admin endpoints
// ============================================================

/** GET /telemetry/orgs — list all orgs with telemetry status (ADMIN only) */
export function getTelemetryOrgs(): Promise<TelemetryOrgInfoDto[]> {
  return api.get<TelemetryOrgInfoDto[]>('/telemetry/orgs');
}

/** PATCH /telemetry/orgs/:orgId — toggle telemetry for an org (ADMIN only) */
export function toggleOrgTelemetry(
  orgId: string,
  enabled: boolean,
): Promise<TelemetryOrgInfoDto> {
  return api.patch<TelemetryOrgInfoDto>(`/telemetry/orgs/${orgId}`, { enabled });
}

/** GET /telemetry/events — query stored events (ADMIN only) */
export function getTelemetryEvents(params?: {
  organizationId?: string;
  type?: TelemetryEventType;
  severity?: TelemetrySeverity;
  sessionId?: string;
  page?: number;
  limit?: number;
}): Promise<{
  data: TelemetryEventResponseDto[];
  total: number;
  page: number;
  limit: number;
}> {
  const query = new URLSearchParams();
  if (params?.organizationId) query.set('organizationId', params.organizationId);
  if (params?.type) query.set('type', params.type);
  if (params?.severity) query.set('severity', params.severity);
  if (params?.sessionId) query.set('sessionId', params.sessionId);
  if (params?.page) query.set('page', String(params.page));
  if (params?.limit) query.set('limit', String(params.limit));
  const qs = query.toString();
  return api.get(`/telemetry/events${qs ? `?${qs}` : ''}`);
}

// ============================================================
// Types
// ============================================================

export interface TelemetryEventInput {
  type: TelemetryEventType;
  severity: TelemetrySeverity;
  message: string;
  stack?: string;
  url?: string;
  method?: string;
  statusCode?: number;
  metadata?: Record<string, unknown>;
  timestamp: string;
}